import type { JSX } from 'react';

import { CheckmarkCircle, WarningCircle } from '@remarkable/ark-icons';
import { Spinner, Typography } from '@remarkable/ark-web';
import type { UseQueryResult } from '@tanstack/react-query';

import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { Subscription } from 'src/api/endpoints/storeApi.types';
import { getPaymentCardExpiresInNumMonths } from 'src/utils/getPaymentCardExpired';
import { URLS } from 'src/utils/urls/urls';
import { useVerifyPaymentMethod } from 'src/utils/useVerifyPaymentMethod';

import { Plural, Trans } from '@lingui/react/macro';
import { ChangeCreditCardButton } from './ChangeCreditCardButton';
import { Link } from './Link';
import { NotificationBanner } from './NotificationBanner';

export const UpdatePaymentCardBanner = ({
  subscription,
}: {
  subscription: UseQueryResult<Subscription>;
}) => {
  const verifyPaymentMethod = useVerifyPaymentMethod();

  const NUM_MONTHS_BEFORE_EXPIRATION_TO_SHOW_BANNER = 3;

  if (subscription.isPending) return null;

  const numMonthsUntilPaymentMethodExpires = getPaymentCardExpiresInNumMonths(
    subscription.data
  );

  if (numMonthsUntilPaymentMethodExpires === null) return null;

  const showBanner =
    numMonthsUntilPaymentMethodExpires <=
      NUM_MONTHS_BEFORE_EXPIRATION_TO_SHOW_BANNER ||
    verifyPaymentMethod.isSuccess;

  if (!showBanner) return null;

  tracker.identify({
    myrm_utm_parameters: JSON.stringify({
      myrm_show_payment_banner: true,
    }),
  });

  const renderNotificationBanner = (content: JSX.Element) => (
    <NotificationBanner data-cy="update-payment-card-banner">
      {content}
    </NotificationBanner>
  );

  if (verifyPaymentMethod.isPending) {
    return renderNotificationBanner(
      <div className="flex flex-row gap-12">
        <Spinner />
        <div className="mt-4">
          <Typography variant="heading-subtle-lg" as="h3">
            <Trans>Updating your payment information</Trans>
          </Typography>
          <Typography variant="body-md" as="p">
            <Trans>Please wait.</Trans>
          </Typography>
        </div>
      </div>
    );
  }

  if (verifyPaymentMethod.isSuccess) {
    return renderNotificationBanner(
      <div className="flex flex-row gap-12">
        <CheckmarkCircle size={32} className="text-feedback-green-500" />
        <div className="mt-4">
          <Typography variant="heading-subtle-lg" as="h3">
            <Trans>Your payment information has been updated</Trans>
          </Typography>
          <Typography variant="body-md" as="p">
            <Trans>
              Go to your{' '}
              <Link
                inline
                to={URLS.SUBSCRIPTION}
                trackingLocation={
                  ComponentLocations.NOTIFICATIONS.UPDATE_PAYMENT_CARD
                }
              >
                Subscription page
              </Link>{' '}
              to see your payment details.
            </Trans>
          </Typography>
        </div>
      </div>
    );
  }

  return renderNotificationBanner(
    <div className="flex flex-col gap-16">
      <div>
        <Typography variant="heading-subtle-lg" as="h3">
          <Plural
            value={numMonthsUntilPaymentMethodExpires}
            zero="Your payment card has expired"
            one="Your payment card is expiring in 1 month"
            other="Your payment card is expiring in # months"
          />
        </Typography>

        <Typography variant="body-md" as="p">
          <Trans>Update your payment card to keep using our services.</Trans>
        </Typography>
      </div>

      <ChangeCreditCardButton
        componentLocation={ComponentLocations.NOTIFICATIONS.UPDATE_PAYMENT_CARD}
      />

      {verifyPaymentMethod.isError && (
        <div className="flex flex-row gap-8 text-feedback-red-500">
          <WarningCircle size={32} />
          <Typography
            variant="body-md"
            className="text-feedback-red-500"
            as="p"
          >
            <Trans>
              We couldn&apos;t update your payment information. Please try again
              later.
            </Trans>
          </Typography>
        </div>
      )}
    </div>
  );
};
